package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.domain.model.IptvSource
import com.iptvplayer.shared.domain.repository.ContentRepository
import com.iptvplayer.shared.presentation.state.SourceUiState
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SourceViewModel(
    private val contentRepository: ContentRepository
) : ViewModel() {

    private val _state = MutableStateFlow(SourceUiState())
    val state: StateFlow<SourceUiState> = _state.asStateFlow()

    init {
        loadSources()
    }

    private fun loadSources() {
        viewModelScope.launch {
            // Pull persisted sources from the backend before observing them.
            launch { contentRepository.syncSources() }
            contentRepository.getSources().collect { sources ->
                _state.update { it.copy(sources = sources, isLoading = false) }
            }
        }
    }

    fun addM3USource(name: String, url: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val source = IptvSource.M3USource(
                id = generateId(),
                name = name,
                url = url
            )
            when (val result = contentRepository.addSource(source)) {
                is Result.Success -> {
                    contentRepository.loadPlaylist(source)
                    _state.update { it.copy(isLoading = false) }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun addXtreamSource(name: String, serverUrl: String, username: String, password: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val source = IptvSource.XtreamSource(
                id = generateId(),
                name = name,
                serverUrl = normalizeServerUrl(serverUrl),
                username = username,
                password = password
            )
            when (val result = contentRepository.addSource(source)) {
                is Result.Success -> {
                    contentRepository.loadPlaylist(source)
                    _state.update { it.copy(isLoading = false) }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    /** Edit an existing source in place (same id → backend upsert + reload its content). */
    fun updateM3USource(id: String, name: String, url: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val source = IptvSource.M3USource(id = id, name = name, url = url)
            when (val result = contentRepository.addSource(source)) {
                is Result.Success -> {
                    contentRepository.loadPlaylist(source)
                    _state.update { it.copy(isLoading = false) }
                }
                is Result.Error -> _state.update { it.copy(isLoading = false, error = result.message) }
                is Result.Loading -> {}
            }
        }
    }

    fun updateXtreamSource(id: String, name: String, serverUrl: String, username: String, password: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val source = IptvSource.XtreamSource(
                id = id,
                name = name,
                serverUrl = normalizeServerUrl(serverUrl),
                username = username,
                password = password
            )
            when (val result = contentRepository.addSource(source)) {
                is Result.Success -> {
                    contentRepository.loadPlaylist(source)
                    _state.update { it.copy(isLoading = false) }
                }
                is Result.Error -> _state.update { it.copy(isLoading = false, error = result.message) }
                is Result.Loading -> {}
            }
        }
    }

    /** Force-refresh all cached content from the backend (Settings → Refresh all). */
    fun refreshAll() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            contentRepository.syncSources(force = true)
            _state.update { it.copy(isLoading = false) }
        }
    }

    fun removeSource(sourceId: String) {
        viewModelScope.launch {
            contentRepository.removeSource(sourceId)
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }

    /**
     * Normalizes an Xtream server URL: adds a scheme when missing and corrects
     * impossible scheme/port combinations that otherwise fail with TLS errors
     * (https on :80 → http, http on :443 → https). Most Xtream panels speak
     * plain HTTP on :80.
     */
    private fun normalizeServerUrl(input: String): String {
        var url = input.trim().trimEnd('/')
        if (!url.startsWith("http://", ignoreCase = true) &&
            !url.startsWith("https://", ignoreCase = true)
        ) {
            url = "http://$url"
        }
        val isHttps = url.startsWith("https://", ignoreCase = true)
        val afterScheme = url.substringAfter("://")
        val port = afterScheme.substringBefore('/').substringAfter(':', "").toIntOrNull()
        return when {
            isHttps && port == 80 -> "http://$afterScheme"
            !isHttps && port == 443 -> "https://$afterScheme"
            else -> url
        }
    }

    private fun generateId(): String {
        return kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toString() +
            (1000..9999).random().toString()
    }
}
